import axios from 'axios'

export interface AuditLogUser {
  id: number
  name?: string
  forename?: string
  surname?: string
  username?: string
  email?: string
}

export interface AuditLogAuditable {
  id: number | string
  title?: string
  name?: string
  [key: string]: any
}

export interface AuditLog {
  id: number
  auditable_type: string | null
  auditable_id: number | string | null
  user_id: number | null
  user_type: string | null
  event: 'created' | 'updated' | 'deleted' | 'restored' | 'viewed'
  old_values: Record<string, any> | null
  new_values: Record<string, any> | null
  changed_attributes: string[] | null
  metadata: Record<string, any> | null
  url: string | null
  ip_address: string | null
  user_agent: string | null
  request_id: string | null
  created_at: string
  user?: AuditLogUser | null
  auditable?: AuditLogAuditable | null
}

export interface AuditLogFilters {
  event?: string
  user_id?: number
  model_type?: string
  model_id?: number
  date_from?: string
  date_to?: string
  search?: string
  per_page?: number
  page?: number
}

export interface AuditLogStats {
  total: number
  by_event: Record<string, number>
  by_model: Record<string, number>
  by_user: Record<string, number>
  recent_activity: Array<{
    id: number
    event: string
    model_type: string
    created_at: string
    user: string
  }>
}

export interface PaginatedAuditLogs {
  data: AuditLog[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number
  to: number
}

export interface AuditLogCreatePayload {
  auditable_type: string | null
  auditable_id: number | string | null
  event: string
  old_values?: Record<string, any> | null
  new_values?: Record<string, any> | null
  changed_attributes?: string[] | null
  metadata?: Record<string, any> | null
}

export default function useAuditLogs() {
  const fetchAuditLogs = async (filters?: AuditLogFilters): Promise<PaginatedAuditLogs> => {
    const params: Record<string, any> = {}

    if (filters?.event) params.event = filters.event
    if (filters?.user_id) params.user_id = filters.user_id
    if (filters?.model_type) params.model_type = filters.model_type
    if (filters?.model_id) params.model_id = filters.model_id
    if (filters?.date_from) params.date_from = filters.date_from
    if (filters?.date_to) params.date_to = filters.date_to
    if (filters?.search) params.search = filters.search
    if (filters?.per_page) params.per_page = filters.per_page
    if (filters?.page) params.page = filters.page

    const { data } = await axios.get<PaginatedAuditLogs>('/api/audit-logs', { params })
    return data
  }

  const fetchAuditLog = async (id: number): Promise<AuditLog> => {
    const { data } = await axios.get<AuditLog>(`/api/audit-logs/${id}`)
    return data
  }

  const fetchAuditLogStats = async (filters?: {
    date_from?: string
    date_to?: string
  }): Promise<AuditLogStats> => {
    const params: Record<string, any> = {}
    if (filters?.date_from) params.date_from = filters.date_from
    if (filters?.date_to) params.date_to = filters.date_to

    const { data } = await axios.get<AuditLogStats>('/api/audit-logs/stats/summary', { params })
    return data
  }

  const logAuditEvent = async (params: {
    auditableType: string | null
    auditableId: number | string | null
    event: string
    oldValues?: Record<string, any> | null
    newValues?: Record<string, any> | null
    changedAttributes?: string[] | null
    metadata?: Record<string, any> | null
  }): Promise<AuditLog> => {
    const payload: AuditLogCreatePayload = {
      auditable_type: params.auditableType,
      auditable_id: params.auditableId,
      event: params.event,
      old_values: params.oldValues ?? null,
      new_values: params.newValues ?? null,
      changed_attributes: params.changedAttributes ?? null,
      metadata: params.metadata ?? null,
    }

    const { data } = await axios.post<AuditLog>('/api/audit-logs', payload)
    return data
  }

  return {
    fetchAuditLogs,
    fetchAuditLog,
    fetchAuditLogStats,
    logAuditEvent,
  }
}
